import type { FC, PropsWithChildren } from 'react';
import type { Locale } from 'content/data';
import { createContext, useContext, useMemo } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { CtaModal } from 'components/cta-modal';

type CasinoAlternativesModalContextValue = {
  openCasinoAlternativesModal: VoidFunction;
};

const CtaContext = createContext<CasinoAlternativesModalContextValue | null>(null);

type CtaProviderProps = PropsWithChildren<{ locale: Locale }>;

export const CtaProvider: FC<CtaProviderProps> = ({ children, locale }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const value = useMemo<CasinoAlternativesModalContextValue>(() => ({ openCasinoAlternativesModal: onOpen }), []);

  return (
    <CtaContext.Provider value={value}>
      {children}
      <CtaModal isOpen={isOpen} onClose={onClose} locale={locale} />
    </CtaContext.Provider>
  );
};

export function useCtaContext(): CasinoAlternativesModalContextValue {
  const context = useContext(CtaContext);

  if (!context) {
    throw new Error('useCtaContext must be used within CtaProvider');
  }

  return context;
}

// LinkButton (used directly by CtaButton, and internally by BonusCard for CtaBonusCard) spreads
// unrecognized props after its own `as={Link} href={url}` defaults, so this override reverts the
// underlying Chakra Button to its native <button> element and wires the click to open the modal,
// instead of rendering as an anchor pointing at a link we never actually navigate to.
type CtaLinkOverrides = {
  as: 'button';
  href: undefined;
  onClick: VoidFunction;
};

export function useCtaLinkProps(): CtaLinkOverrides {
  const { openCasinoAlternativesModal } = useCtaContext();

  return {
    as: 'button',
    href: undefined,
    onClick: openCasinoAlternativesModal,
  };
}
